import React, { useCallback, useEffect, useState } from "react";
import {
    Drawer,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Tooltip,
    Button,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import ChatIcon from "@material-ui/icons/Chat";
import DoneOutlinedIcon from "@material-ui/icons/DoneOutlined";
import DoneAllOutlinedIcon from "@material-ui/icons/DoneAllOutlined";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import ArrowDownwardOutlinedIcon from "@material-ui/icons/ArrowDownwardOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import moment from "moment";
import HttpService from "../services/httpService";

// Self-contained BotWot WhatsApp conversation viewer.
//
// The same thread the lead page shows under "BotWot Chats", extracted so the
// WhatsApp funnel dashboard can open it from a table row without duplicating
// ~200 lines of chat rendering. It owns its own fetch: the caller passes a
// leadId and this loads when it opens, so no parent needs to hold chat state.
const WA_DARK = "#075E54";
const WA_DEEP = "#054C44";

const useStyles = makeStyles(() => ({
    paper: { width: 560, maxWidth: "100%" },
    container: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        background: "#ECE5DD",
        // Faint cross-hatch so the thread reads as a chat surface rather than
        // a flat grey panel - very low contrast so bubbles stay dominant.
        backgroundImage:
            "radial-gradient(rgba(0,0,0,0.045) 1px, transparent 1px), radial-gradient(rgba(0,0,0,0.045) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
        backgroundPosition: "0 0, 11px 11px",
    },
    header: {
        padding: "12px 14px",
        background: `linear-gradient(135deg, ${WA_DEEP} 0%, ${WA_DARK} 55%, #0b7f6a 100%)`,
        color: "#fff",
        flexShrink: 0,
        boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
        zIndex: 2,
    },
    avatar: {
        width: 42,
        height: 42,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontWeight: 800,
        fontSize: 15,
        background: "rgba(255,255,255,0.18)",
        border: "2px solid rgba(255,255,255,0.32)",
    },
    headerMeta: { display: "flex", alignItems: "center", gap: 5, fontSize: 12, opacity: 0.88, marginTop: 2 },
    headerChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 9px",
        borderRadius: 999,
        background: "rgba(0,0,0,0.26)",
        border: "1px solid rgba(255,255,255,0.22)",
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    searchField: {
        marginTop: 10,
        "& .MuiOutlinedInput-root": { borderRadius: 999, background: "rgba(255,255,255,0.14)", color: "#fff", fontSize: 13 },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.28)" },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.5)" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#25D366" },
        "& input::placeholder": { color: "rgba(255,255,255,0.7)", opacity: 1 },
    },
    body: { flex: 1, overflowY: "auto", padding: "14px 12px 18px", scrollBehavior: "smooth" },
    dayDivider: { display: "flex", justifyContent: "center", margin: "14px 0 12px" },
    dayLabel: {
        padding: "4px 13px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.92)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        fontSize: 11,
        fontWeight: 700,
        color: "#5b6b66",
        textTransform: "uppercase",
    },
    row: { display: "flex", marginBottom: 10 },
    left: { justifyContent: "flex-start" },
    right: { justifyContent: "flex-end" },
    bubble: {
        position: "relative",
        maxWidth: "78%",
        minWidth: 110,
        padding: "8px 11px 6px",
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.45,
        boxShadow: "0 1px 1.5px rgba(0,0,0,0.13)",
        transition: "box-shadow .18s ease",
        "&:hover": { boxShadow: "0 3px 10px rgba(0,0,0,0.16)" },
        "&::after": { content: '""', position: "absolute", top: 0, width: 0, height: 0, borderStyle: "solid" },
    },
    leftBubble: {
        background: "#fff",
        borderTopLeftRadius: 2,
        "&::after": { left: -7, borderWidth: "0 8px 9px 0", borderColor: "transparent #fff transparent transparent" },
    },
    rightBubble: {
        background: "#DCF8C6",
        borderTopRightRadius: 2,
        "&::after": { right: -7, borderWidth: "0 0 9px 8px", borderColor: "transparent transparent transparent #DCF8C6" },
    },
    sender: { fontSize: 11, fontWeight: 700, marginBottom: 3 },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 5,
        marginTop: 3,
        fontSize: 10,
        color: "rgba(0,0,0,0.45)",
    },
    hit: { background: "#FFF3B0", borderRadius: 3, padding: "0 1px" },
    jumpLatest: {
        position: "absolute",
        right: 18,
        bottom: 18,
        zIndex: 3,
        width: 40,
        height: 40,
        background: "#fff",
        color: WA_DARK,
        boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
        "&:hover": { background: "#f1f5f4" },
    },
    image: {
        width: "100%",
        borderRadius: 8,
        display: "block",
        cursor: "zoom-in",
        transition: "filter .18s ease",
        "&:hover": { filter: "brightness(0.94)" },
    },
    emptyState: {
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        color: "#5b6b66",
        textAlign: "center",
        padding: 20,
    },
    buttonStyle: { marginTop: 5, display: "block" },
}));

// Same convention as the server's maskPhone: everything hidden except the last
// two, length preserved. Values that already carry an X are left alone rather
// than masked twice.
const maskPhone = (value) => {
    const str = String(value ?? "").trim();
    if (!str) return "";
    if (/x/i.test(str)) return str;
    if (str.length <= 2) return "XX";
    return `${"X".repeat(str.length - 2)}${str.slice(-2)}`;
};

const initialsOf = (name) => {
    const text = String(name || "").trim();
    if (!text) return "?";
    return text.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
};

const dayLabelOf = (value) => {
    const d = moment(value);
    if (!d.isValid()) return "";
    if (d.isSame(moment(), "day")) return "Today";
    if (d.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    return d.format("DD MMM YYYY");
};

const timeOf = (value) => (moment(value).isValid() ? moment(value).format("h:mma") : "");

const prettyLabel = (text) =>
    String(text || "")
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

// A WhatsApp Flow response is whatever JSON the flow posted back
// (routes/automationLead.js JSON.parses responseJson straight through), so a
// value can be a string, a number, a boolean, an array of selections, or an
// object like {id, title}. Calling String() on the last two is what rendered
// "[object Object]" in the bubble.
// Keys that hold the human-readable part of a wrapped value, most content-like
// first. WhatsApp wraps message text as {body}, Flow pickers as {id, title},
// and some payloads nest a further level ({body: {text}}) - hence the recursion
// below rather than a single lookup.
const LABEL_KEYS = ["body", "text", "title", "name", "label", "caption", "message", "value"];

const formatFlowValue = (value, depth = 0) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return value.map((v) => formatFlowValue(v, depth + 1)).filter(Boolean).join(", ");
    if (typeof value === "object") {
        // Depth cap: JSON-parsed data can't be cyclic, but a deeply nested blob
        // would otherwise recurse further than is useful in a chat bubble.
        if (depth < 4) {
            for (const key of LABEL_KEYS) {
                if (value[key] === undefined || value[key] === null) continue;
                const unwrapped = formatFlowValue(value[key], depth + 1);
                if (unwrapped) return unwrapped;
            }
        }
        try {
            return JSON.stringify(value);
        } catch (e) {
            return "";
        }
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
};

// `data` is only a set of answers when it is a plain object. A parsed value
// that came back as a string would turn into one entry per CHARACTER under
// Object.entries, so anything else is rendered as a single value.
const flowEntries = (data) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) return null;
    return Object.entries(data);
};

// Only the fields a human would read - matching a URL or mime type would make
// for confusing search hits.
const messageText = (msg) => {
    const c = msg?.content || {};
    const parts = [formatFlowValue(c.text), formatFlowValue(c.caption), msg?.senderName];
    const entries = flowEntries(c.data);
    if (entries) entries.forEach(([k, v]) => parts.push(k, formatFlowValue(v)));
    else if (c.data !== undefined) parts.push(formatFlowValue(c.data));
    if (Array.isArray(c.buttons)) c.buttons.forEach((b) => parts.push(b?.title));
    return parts.filter(Boolean).join(" ");
};

const BotWotChatDrawer = ({ open, onClose, leadId, leadNo, customerName }) => {
    const classes = useStyles();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [showJump, setShowJump] = useState(false);
    const [headerName, setHeaderName] = useState("");
    const [headerPhone, setHeaderPhone] = useState("");

    const bodyId = `botwotChatBody-${leadId || "none"}`;

    const fetchChat = useCallback(async () => {
        if (!leadId) return;
        setLoading(true);
        setError("");
        try {
            const res = await HttpService.getBotWotChatConversations({ leadId });
            const data = res?.data?.data || [];
            setMessages(data);
            if (data.length) {
                setHeaderName(data[0].displayName || customerName || "Customer");
                // Masked before it reaches state, so the plain number is never
                // held anywhere the UI could render it by accident.
                setHeaderPhone(maskPhone(data[0].displayNumber));
            } else {
                setHeaderName(customerName || "Customer");
                setHeaderPhone("");
            }
        } catch (e) {
            setMessages([]);
            setError(e?.response?.data?.message || "Could not load this conversation");
        }
        setLoading(false);
    }, [leadId, customerName]);

    // Reset per lead, then load. Without the reset the previous lead's thread
    // would flash in the drawer before the new one arrives.
    useEffect(() => {
        if (!open) return;
        setMessages([]);
        setSearch("");
        setShowJump(false);
        fetchChat();
    }, [open, fetchChat]);

    useEffect(() => {
        if (!open) return;
        const el = document.getElementById(bodyId);
        if (!el) return;
        const t = setTimeout(() => { el.scrollTop = el.scrollHeight; }, 120);
        return () => clearTimeout(t);
    }, [messages, open, bodyId]);

    const term = search.trim().toLowerCase();
    const filtered = term ? messages.filter((m) => messageText(m).toLowerCase().includes(term)) : messages;

    const highlight = (text) => {
        // Coerced through formatFlowValue, not String(): a malformed payload can
        // put an object in a `text` field too, and String() would print
        // "[object Object]" there just as it did for flow answers.
        const str = typeof text === "string" ? text : formatFlowValue(text);
        if (!term) return str;
        // Escaped: a stray "(" in the search box would otherwise throw on an
        // invalid RegExp.
        const escaped = search.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return str.split(new RegExp(`(${escaped})`, "ig")).map((piece, i) =>
            piece.toLowerCase() === term ? <span key={i} className={classes.hit}>{piece}</span> : piece
        );
    };

    const showsDivider = (list, i) => {
        if (i === 0) return true;
        const prev = list[i - 1]?.timestamp;
        const curr = list[i]?.timestamp;
        if (!prev || !curr) return false;
        return !moment(prev).isSame(moment(curr), "day");
    };

    const renderTicks = (msg) => {
        if (msg.direction === "left") return null;
        const tick = (title, node) => (
            <Tooltip title={title}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>{node}</span>
            </Tooltip>
        );
        if (msg.status === "sent") return tick("Sent", <DoneOutlinedIcon style={{ fontSize: 14 }} />);
        if (msg.status === "delivered") return tick("Delivered", <DoneAllOutlinedIcon style={{ fontSize: 14 }} />);
        if (msg.status === "read") return tick("Read", <DoneAllOutlinedIcon style={{ fontSize: 14, color: "#34B7F1" }} />);
        if (msg.status === "failed") {
            return tick("Failed to send", <ErrorOutlineOutlinedIcon style={{ fontSize: 14, color: "#d03b3b" }} />);
        }
        return null;
    };

    const renderMessage = (msg) => {
        const c = msg?.content || {};
        const hl = (t) => highlight(t);
        const body = { fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" };

        switch (c.type) {
            case "text":
                return <Typography style={body}>{hl(c.text)}</Typography>;
            case "image":
                return (
                    <>
                        <img
                            src={c.url}
                            alt={c.caption || "Shared image"}
                            className={classes.image}
                            onClick={() => c.url && window.open(c.url, "_blank", "noopener,noreferrer")}
                        />
                        {(c.caption || c.text) && (
                            <Typography style={{ ...body, marginTop: 4 }}>{hl(c.caption || c.text)}</Typography>
                        )}
                    </>
                );
            case "audio":
                return (
                    <>
                        <audio controls style={{ width: "100%" }}>
                            <source src={c.url} type={c.mimeType} />
                            Your browser does not support audio
                        </audio>
                        {c.text && <Typography style={{ ...body, marginTop: 4 }}>{hl(c.text)}</Typography>}
                    </>
                );
            case "video":
                return (
                    <>
                        <video controls style={{ width: "100%", borderRadius: 8 }}>
                            <source src={c.url} type={c.mimeType} />
                        </video>
                        {c.caption && <Typography style={{ ...body, marginTop: 4 }}>{hl(c.caption)}</Typography>}
                    </>
                );
            case "document":
                return (
                    <>
                        <a
                            href={c.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 7,
                                padding: "7px 11px",
                                borderRadius: 8,
                                background: "rgba(0,0,0,0.05)",
                                color: WA_DARK,
                                fontWeight: 600,
                                fontSize: 12.5,
                                textDecoration: "none",
                            }}
                        >
                            📄 {c.filename || "Download document"}
                        </a>
                        {c.caption && <Typography style={{ ...body, marginTop: 4 }}>{hl(c.caption)}</Typography>}
                    </>
                );
            case "interactive":
            case "template":
                return (
                    <>
                        <Typography style={body}>{hl(c.text)}</Typography>
                        {c.buttons?.map((btn, i) => (
                            <Button key={btn.id || i} size="small" variant="outlined" className={classes.buttonStyle}>
                                {prettyLabel(btn.title)}
                            </Button>
                        ))}
                    </>
                );
            case "flow_response": {
                // A form the customer filled in - labelled rows read as answers
                // rather than a run-on paragraph.
                const entries = flowEntries(c.data);
                if (!entries) {
                    return <Typography style={body}>{hl(formatFlowValue(c.data))}</Typography>;
                }
                const answered = entries.filter(([, v]) => formatFlowValue(v) !== "");
                if (!answered.length) {
                    return <Typography style={{ ...body, opacity: 0.7 }}>Form submitted with no answers</Typography>;
                }
                return (
                    <div style={{ display: "grid", gap: 5 }}>
                        {answered.map(([k, v]) => (
                            <div key={k} style={{ fontSize: 12.5 }}>
                                <span style={{ color: "rgba(0,0,0,0.5)", fontWeight: 600 }}>{prettyLabel(k)}:</span>{" "}
                                <span style={{ fontWeight: 600, wordBreak: "break-word" }}>{hl(formatFlowValue(v))}</span>
                            </div>
                        ))}
                    </div>
                );
            }
            case "button_reply":
            case "button":
                return <Typography style={{ ...body, fontWeight: 700 }}>↩ {hl(c.text)}</Typography>;
            default:
                return <Typography style={{ ...body, opacity: 0.7 }}>Unsupported message type</Typography>;
        }
    };

    const replyCount = messages.filter((m) => m.direction === "left").length;

    return (
        <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
            <div className={classes.container}>
                <div className={classes.header}>
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                        <div className={classes.avatar}>{initialsOf(headerName || customerName)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }} noWrap>
                                {headerName || customerName || "Customer"}
                            </Typography>
                            <div className={classes.headerMeta}>
                                <LockOutlinedIcon style={{ fontSize: 12 }} />
                                <span style={{ letterSpacing: 0.5 }}>{headerPhone || "No number on file"}</span>
                                {leadNo ? <span style={{ opacity: 0.7 }}>· {leadNo}</span> : null}
                            </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                            {!loading && messages.length > 0 && (
                                <span className={classes.headerChip}>
                                    <ChatIcon style={{ fontSize: 12 }} />
                                    {term ? `${filtered.length}/${messages.length}` : `${replyCount} repl${replyCount === 1 ? "y" : "ies"}`}
                                </span>
                            )}
                            {leadId && (
                                <Tooltip title="Open this lead's page in a new tab">
                                    <IconButton
                                        size="small"
                                        style={{ color: "#fff" }}
                                        onClick={() => window.open(`/project-logs/${leadId}`, "_blank", "noopener,noreferrer")}
                                    >
                                        <OpenInNewIcon style={{ fontSize: 17 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton size="small" style={{ color: "#fff" }} onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </div>

                    {!loading && messages.length > 0 && (
                        <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder="Search in this conversation…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={classes.searchField}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon style={{ fontSize: 17, color: "rgba(255,255,255,0.75)" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearch("")} style={{ color: "rgba(255,255,255,0.8)" }}>
                                            <CloseIcon style={{ fontSize: 15 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    )}
                </div>

                <div
                    id={bodyId}
                    className={classes.body}
                    onScroll={(e) => {
                        const el = e.currentTarget;
                        setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
                    }}
                >
                    {loading ? (
                        // Placeholder bubbles alternating sides, so the thread
                        // already looks like a conversation while it loads.
                        [72, 58, 84, 46, 66, 52].map((w, i) => (
                            <div key={i} className={`${classes.row} ${i % 2 === 0 ? classes.left : classes.right}`}>
                                <Skeleton variant="rect" width={`${w}%`} height={i % 3 === 0 ? 62 : 44} style={{ borderRadius: 10 }} />
                            </div>
                        ))
                    ) : error ? (
                        <div className={classes.emptyState}>
                            <ErrorOutlineOutlinedIcon style={{ fontSize: 38, opacity: 0.4 }} />
                            <Typography style={{ marginTop: 10, fontWeight: 500 }}>{error}</Typography>
                            <Button size="small" onClick={fetchChat} style={{ marginTop: 8 }}>Try again</Button>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className={classes.emptyState}>
                            <ChatIcon style={{ fontSize: 40, opacity: 0.3 }} />
                            <Typography style={{ marginTop: 10, fontWeight: 500 }}>No messages yet</Typography>
                            <Typography style={{ fontSize: 12, opacity: 0.7 }}>
                                Conversations with this customer will appear here
                            </Typography>
                        </div>
                    ) : filtered.length === 0 ? (
                        // Distinct from "no messages", which would otherwise read
                        // as the thread having vanished.
                        <div className={classes.emptyState}>
                            <SearchOutlinedIcon style={{ fontSize: 38, opacity: 0.3 }} />
                            <Typography style={{ marginTop: 10, fontWeight: 500 }}>
                                No messages match “{search.trim()}”
                            </Typography>
                            <Button size="small" onClick={() => setSearch("")} style={{ marginTop: 8 }}>Clear search</Button>
                        </div>
                    ) : (
                        filtered.map((msg, i) => {
                            const isLeft = msg.direction === "left";
                            const prev = filtered[i - 1];
                            // Repeating the sender on consecutive bubbles from the
                            // same side is noise - show it only when it changes.
                            const showSender = !prev || prev.direction !== msg.direction || prev.senderName !== msg.senderName;
                            return (
                                <React.Fragment key={msg.id || msg.messageId || i}>
                                    {showsDivider(filtered, i) && (
                                        <div className={classes.dayDivider}>
                                            <span className={classes.dayLabel}>{dayLabelOf(msg.timestamp)}</span>
                                        </div>
                                    )}
                                    <div className={`${classes.row} ${isLeft ? classes.left : classes.right}`}>
                                        <div className={`${classes.bubble} ${isLeft ? classes.leftBubble : classes.rightBubble}`}>
                                            {showSender && (
                                                <div className={classes.sender} style={{ color: isLeft ? "#0b7f6a" : "#1f7a4d" }}>
                                                    {msg.senderName}
                                                </div>
                                            )}
                                            {renderMessage(msg)}
                                            <div className={classes.footer}>
                                                <span>{timeOf(msg.timestamp)}</span>
                                                {renderTicks(msg)}
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })
                    )}
                </div>

                {showJump && !loading && (
                    <Tooltip title="Jump to latest message">
                        <IconButton
                            className={classes.jumpLatest}
                            onClick={() => {
                                const el = document.getElementById(bodyId);
                                if (el) el.scrollTop = el.scrollHeight;
                            }}
                        >
                            <ArrowDownwardOutlinedIcon style={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
        </Drawer>
    );
};

export default BotWotChatDrawer;
